import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, Plus, Search, MoreHorizontal, Edit, Trash2, Eye,
  CheckCircle2, XCircle, Loader2, Users, BookOpen, GraduationCap,
  DollarSign, Filter, ChevronRight, Globe, Activity, Power, PowerOff
} from "lucide-react";
import { toast } from "sonner";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { tenantAPI } from "../../lib/api";
import api from "../../lib/api";
import { cn } from "../../lib/utils";

const STATUS_COLORS = { active: "bg-green-50 text-green-700 border-green-200", inactive: "bg-red-50 text-red-700 border-red-200", deleted: "bg-gray-100 text-gray-500 border-gray-200" };
const PLAN_COLORS = { free: "bg-gray-100 text-gray-700", starter: "bg-blue-50 text-blue-700", professional: "bg-violet-50 text-violet-700", enterprise: "bg-amber-50 text-amber-700", basic: "bg-blue-50 text-blue-700" };

const TenantsPage = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPlan, setFilterPlan] = useState("all");

  // Create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", subdomain: "", plan: "starter", primary_color: "#1B4F8A", industry: "manufacturing" });
  const [creating, setCreating] = useState(false);

  // Stats
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, totalUsers: 0 });

  const fetchTenants = async () => {
    try {
      const r = await tenantAPI.list();
      const data = r.data || [];
      setTenants(data);
      setStats({
        total: data.length,
        active: data.filter(t => t.status === "active").length,
        inactive: data.filter(t => t.status !== "active").length,
        totalUsers: 0, // We'll get this from individual tenant analytics if needed
      });
    } catch { toast.error("Failed to load tenants"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTenants(); }, []);

  const filtered = tenants.filter(t =>
    (filterStatus === "all" || t.status === filterStatus) &&
    (filterPlan === "all" || t.plan === filterPlan) &&
    (!search || t.name?.toLowerCase().includes(search.toLowerCase()) || t.subdomain?.toLowerCase().includes(search.toLowerCase()))
  );

  const handleCreate = async () => {
    if (!createForm.name || !createForm.subdomain) { toast.error("Name and subdomain required"); return; }
    setCreating(true);
    try {
      await tenantAPI.create(createForm);
      toast.success("Tenant created");
      setShowCreate(false);
      setCreateForm({ name: "", subdomain: "", plan: "starter", primary_color: "#1B4F8A", industry: "manufacturing" });
      fetchTenants();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    finally { setCreating(false); }
  };

  return (
    <AdminLayout title="Tenants">
      <div className="space-y-5 max-w-7xl" data-testid="tenants-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" data-testid="tenants-title">Tenant Management</h1>
            <p className="text-sm text-muted-foreground">Manage all tenant organizations on the Odesie platform</p>
          </div>
          <Button className="gap-1.5 h-8 text-xs" onClick={() => setShowCreate(true)} data-testid="create-tenant-btn">
            <Plus className="w-3.5 h-3.5" /> New Tenant
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border shadow-none"><CardContent className="p-3 flex items-center gap-2.5">
            <Building2 className="w-4 h-4 text-primary" /><div><p className="text-lg font-bold">{stats.total}</p><p className="text-[10px] text-muted-foreground">Total Tenants</p></div>
          </CardContent></Card>
          <Card className="border shadow-none"><CardContent className="p-3 flex items-center gap-2.5">
            <Activity className="w-4 h-4 text-green-600" /><div><p className="text-lg font-bold">{stats.active}</p><p className="text-[10px] text-muted-foreground">Active</p></div>
          </CardContent></Card>
          <Card className="border shadow-none"><CardContent className="p-3 flex items-center gap-2.5">
            <XCircle className="w-4 h-4 text-red-500" /><div><p className="text-lg font-bold">{stats.inactive}</p><p className="text-[10px] text-muted-foreground">Inactive/Deleted</p></div>
          </CardContent></Card>
          <Card className="border shadow-none"><CardContent className="p-3 flex items-center gap-2.5">
            <DollarSign className="w-4 h-4 text-amber-600" /><div><p className="text-lg font-bold">{tenants.filter(t => t.plan === "enterprise").length}</p><p className="text-[10px] text-muted-foreground">Enterprise</p></div>
          </CardContent></Card>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input placeholder="Search tenants..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-xs" data-testid="search-tenants" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-8 text-xs w-28"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="deleted">Deleted</SelectItem></SelectContent>
          </Select>
          <Select value={filterPlan} onValueChange={setFilterPlan}>
            <SelectTrigger className="h-8 text-xs w-28"><SelectValue placeholder="Plan" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Plans</SelectItem><SelectItem value="free">Free</SelectItem><SelectItem value="starter">Starter</SelectItem><SelectItem value="professional">Professional</SelectItem><SelectItem value="enterprise">Enterprise</SelectItem></SelectContent>
          </Select>
        </div>

        {/* Tenant List */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <Card className="border shadow-none"><CardContent className="py-16 text-center">
            <Building2 className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" />
            <p className="text-sm font-medium">No tenants found</p>
          </CardContent></Card>
        ) : (
          <Card className="border shadow-none overflow-hidden" data-testid="tenants-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="border-b bg-muted/30">
                  <th className="p-3 text-left text-[10px] font-semibold uppercase text-muted-foreground">Organization</th>
                  <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Plan</th>
                  <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Industry</th>
                  <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Status</th>
                  <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Created</th>
                  <th className="p-3 text-right text-[10px] font-semibold uppercase text-muted-foreground">Actions</th>
                </tr></thead>
                <tbody>
                  {filtered.map(t => (
                    <tr key={t.id} className="border-b hover:bg-muted/20 cursor-pointer" onClick={() => navigate(`/admin/tenants/${t.id}`)} data-testid={`tenant-row-${t.id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-sm font-bold shrink-0" style={{ backgroundColor: t.primary_color || "#1B4F8A" }}>
                            {t.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-medium">{t.name}</p>
                            <p className="text-[10px] text-muted-foreground">{t.subdomain}.odesie.com</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-center"><Badge className={cn("text-[9px] px-1.5 py-0 capitalize", PLAN_COLORS[t.plan])}>{t.plan}</Badge></td>
                      <td className="p-3 text-center"><span className="text-xs text-muted-foreground capitalize">{t.industry}</span></td>
                      <td className="p-3 text-center"><Badge className={cn("text-[9px] px-1.5 py-0 border", STATUS_COLORS[t.status])}>{t.status}</Badge></td>
                      <td className="p-3 text-center"><span className="text-xs text-muted-foreground">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "-"}</span></td>
                      <td className="p-3 text-right" onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-7 text-[10px] gap-1" onClick={() => navigate(`/admin/tenants/${t.id}`)} data-testid={`view-tenant-${t.id}`}>
                          <Eye className="w-3 h-3" /> View <ChevronRight className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Create Tenant Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-base">New Tenant</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Organization Name</Label><Input value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})} className="h-8 text-sm" data-testid="new-tenant-name" /></div>
            <div><Label className="text-xs">Subdomain</Label><div className="flex items-center gap-1"><Input value={createForm.subdomain} onChange={e => setCreateForm({...createForm, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")})} className="h-8 text-sm" data-testid="new-tenant-subdomain" /><span className="text-xs text-muted-foreground whitespace-nowrap">.odesie.com</span></div></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Plan</Label>
                <Select value={createForm.plan} onValueChange={v => setCreateForm({...createForm, plan: v})}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="free">Free</SelectItem><SelectItem value="starter">Starter</SelectItem><SelectItem value="professional">Professional</SelectItem><SelectItem value="enterprise">Enterprise</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Industry</Label><Input value={createForm.industry} onChange={e => setCreateForm({...createForm, industry: e.target.value})} className="h-8 text-sm" /></div>
            </div>
            <div><Label className="text-xs">Brand Color</Label><div className="flex gap-2"><Input type="color" value={createForm.primary_color} onChange={e => setCreateForm({...createForm, primary_color: e.target.value})} className="h-8 w-12 p-0.5 cursor-pointer" /><Input value={createForm.primary_color} onChange={e => setCreateForm({...createForm, primary_color: e.target.value})} className="h-8 text-xs flex-1" /></div></div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button size="sm" className="text-xs gap-1" onClick={handleCreate} disabled={creating} data-testid="save-tenant-btn">{creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default TenantsPage;
